import { defineStore, acceptHMRUpdate } from "pinia";
import jwtDecode from "jwt-decode";
import { useAuthStore } from "@/features/auth/useAuthStore";
import { useLogger } from "@/features/core-shared/composables/useLogger";
import { TokenPermissionRecord } from "../types/token-permissions.types";

const decodeToken = (token: string) => {
  try {
    return jwtDecode<{ authorities?: string | string[] }>(token);
  } catch (error) {
    useLogger().logError("decode-token", error);
    return null;
  }
};

const extractPermissionsFromToken = (token: string | undefined): string[] => {
  if (!token) {
    return [];
  }

  const decodedToken = decodeToken(token);
  const authorities = decodedToken?.authorities;

  if (!authorities) {
    return [];
  }

  if (typeof authorities === "string") {
    return authorities.split(",").map((p) => p.trim());
  }

  if (Array.isArray(authorities)) {
    return authorities;
  }

  return [];
};

export const useTokenPermissionsStore = defineStore("token-permissions-store", () => {
  const auth = useAuthStore();

  const config = useRuntimeConfig();

  // Use a dedicated public flag to explicitly enable test permissions (default false)
  const enableTestPermissions = config.public.ENABLE_TEST_PERMISSIONS === true;
  const testPermissions =
    (config.public.TEST_PERMISSIONS as string | undefined)
      ?.split(",")
      .map((p) => p.trim())
      .filter(Boolean) ?? [];

  const permissionsList = computed<string[]>(() => {
    if (enableTestPermissions) {
      return testPermissions;
    }
    return extractPermissionsFromToken(auth.bearerToken);
  });

  const tokenPermissions = computed<TokenPermissionRecord>(() => {
    const permissions: TokenPermissionRecord = {};

    permissionsList.value.forEach((permission) => {
      permissions[permission] = true;
    });

    return permissions;
  });

  const hasPermission = (permission: string): boolean => {
    return !!tokenPermissions.value[permission];
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some((permission) => hasPermission(permission));
  };

  return {
    tokenPermissions,
    permissionsList,
    hasPermission,
    hasAnyPermission,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTokenPermissionsStore, import.meta.hot));
}
